import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ConstructionIcon from '@mui/icons-material/Construction';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Payout, Client } from '../types';
import { get, post, put, del } from '../services/api.service';
import { clientService } from '../services/client.service';
import { useAuth } from '../contexts/AuthContext';
import { useReadOnlyAction } from '../hooks/useReadOnlyAction';
import { formatCurrency, formatDate, getTodayDate } from '../utils/formatters';

// Validation schema
const payoutSchema = z.object({
  clientId: z.string().min(1, 'Client is required'),
  amount: z.number(),
  date: z.string().min(1, 'Date is required'),
  lossCarryForward: z.number().min(0).optional(),
  notes: z.string().optional(),
});

type PayoutFormData = z.infer<typeof payoutSchema>;

export function PayoutManagement() {
  const { isSuperAdmin, isAdmin } = useAuth();
  const { isReadOnly, snackbarOpen, closeSnackbar, message, showWorkInProgress } = useReadOnlyAction('payouts');
  const [, setLoading] = useState(true);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPayout, setEditingPayout] = useState<Payout | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [payoutToDelete, setPayoutToDelete] = useState<Payout | null>(null);

  // Admin always sees action buttons, but in read-only mode they show "Work in Progress"
  const showActionButtons = isAdmin || isSuperAdmin;

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PayoutFormData>({
    resolver: zodResolver(payoutSchema),
    defaultValues: {
      clientId: '',
      amount: 0,
      date: getTodayDate(),
      lossCarryForward: 0,
      notes: '',
    },
  });

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [payoutsData, clientsData] = await Promise.all([
          get<{ payouts: Payout[] }>('/payouts'),
          clientService.getClients(),
        ]);
        setPayouts(payoutsData.payouts);
        setClients(clientsData);
      } catch (err: any) {
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleOpenDialog = (payout?: Payout) => {
    // Check read-only mode
    if (isReadOnly) {
      showWorkInProgress();
      return;
    }

    if (payout) {
      setEditingPayout(payout);
      reset({
        clientId: payout.clientId,
        amount: payout.amount,
        date: payout.date.split('T')[0],
        lossCarryForward: payout.lossCarryForward,
        notes: payout.notes,
      });
    } else {
      setEditingPayout(null);
      reset({
        clientId: '',
        amount: 0,
        date: getTodayDate(),
        lossCarryForward: 0,
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPayout(null);
    reset();
  };

  const onSubmit = async (data: PayoutFormData) => {
    try {
      if (editingPayout) {
        // Update existing payout
        const response = await put<{ payout: Payout }>(
          `/payouts/${editingPayout.payoutId}?clientId=${editingPayout.clientId}`,
          data
        );
        setPayouts((prev) =>
          prev.map((p) =>
            p.payoutId === editingPayout.payoutId ? response.payout : p
          )
        );
        setSuccess('Payout updated successfully');
      } else {
        // Create new payout
        const response = await post<{ payout: Payout }>('/payouts', data);
        setPayouts((prev) => [response.payout, ...prev]);
        setSuccess('Payout created successfully');
      }
      handleCloseDialog();
    } catch (err: any) {
      setError(err.message || 'Failed to save payout');
    }
  };

  const handleOpenDeleteDialog = (payout: Payout) => {
    if (isReadOnly) {
      showWorkInProgress();
      return;
    }
    setPayoutToDelete(payout);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!payoutToDelete) return;

    try {
      await del(`/payouts/${payoutToDelete.payoutId}?clientId=${payoutToDelete.clientId}`);
      setPayouts((prev) => prev.filter((p) => p.payoutId !== payoutToDelete.payoutId));
      setSuccess('Payout deleted successfully');
      setDeleteDialogOpen(false);
      setPayoutToDelete(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete payout');
    }
  };

  // Column definitions
  const columnDefs: ColDef<Payout>[] = [
    {
      headerName: 'Date',
      field: 'date',
      width: 120,
      sortable: true,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: 'Client',
      field: 'clientName',
      flex: 1,
      minWidth: 150,
      sortable: true,
      filter: true,
    },
    {
      headerName: 'Amount',
      field: 'amount',
      width: 150,
      sortable: true,
      cellRenderer: (params: ICellRendererParams<Payout>) => {
        const value = params.value as number;
        return (
          <span style={{ color: value >= 0 ? '#4caf50' : '#f44336', fontWeight: 600 }}>
            {formatCurrency(value)}
          </span>
        );
      },
    },
    {
      headerName: 'Loss C/F',
      field: 'lossCarryForward',
      width: 120,
      sortable: true,
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      headerName: 'Notes',
      field: 'notes',
      flex: 1,
      minWidth: 200,
    },
    {
      headerName: 'Actions',
      width: 100,
      cellRenderer: (params: ICellRendererParams<Payout>) => {
        if (!showActionButtons) return null;
        const payout = params.data;
        if (!payout) return null;

        return (
          <Box display="flex" gap={0.5}>
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(payout)}
              sx={isReadOnly ? { opacity: 0.7 } : undefined}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleOpenDeleteDialog(payout)}
              sx={isReadOnly ? { opacity: 0.7 } : undefined}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Payout Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage client payouts and profit sharing
            {isReadOnly && ' (Read-only mode)'}
          </Typography>
        </Box>
        {showActionButtons && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={isReadOnly ? { opacity: 0.8 } : undefined}
          >
            New Payout
          </Button>
        )}
      </Box>

      {/* Alerts */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      {/* Read-only mode snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={closeSnackbar}
          severity="info"
          icon={<ConstructionIcon />}
          sx={{
            width: '100%',
            background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
            border: '1px solid #F59E0B',
            color: '#92400E',
            '& .MuiAlert-icon': {
              color: '#D97706',
            },
          }}
        >
          {message}
        </Alert>
      </Snackbar>

      {/* Payouts Grid */}
      <Card>
        <CardContent>
          <Box
            className="ag-theme-alpine"
            sx={{
              height: 500,
              width: '100%',
              '& .ag-header-cell-label': {
                fontWeight: 600,
              },
            }}
          >
            <AgGridReact<Payout>
              rowData={payouts}
              columnDefs={columnDefs}
              animateRows
              pagination
              paginationPageSize={25}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPayout ? 'Edit Payout' : 'New Payout'}</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="clientId"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      label="Client"
                      error={!!errors.clientId}
                      helperText={errors.clientId?.message}
                      disabled={!!editingPayout}
                    >
                      {clients.map((client) => (
                        <MenuItem key={client.clientId} value={client.clientId}>
                          {client.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Amount"
                  {...register('amount', { valueAsNumber: true })}
                  error={!!errors.amount}
                  helperText={errors.amount?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date"
                  InputLabelProps={{ shrink: true }}
                  {...register('date')}
                  error={!!errors.date}
                  helperText={errors.date?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Loss Carry Forward"
                  {...register('lossCarryForward', { valueAsNumber: true })}
                  error={!!errors.lossCarryForward}
                  helperText={errors.lossCarryForward?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Notes"
                  {...register('notes')}
                  error={!!errors.notes}
                  helperText={errors.notes?.message}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingPayout ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this payout? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PayoutManagement;
